import type { TeamAcronym } from '@glovo/organisation'
import { useCallback, useContext } from 'react'
import { ErrorBoundaryContext } from './ErrorBoundaryContext'
import { onError } from './event-handlers'
import type { Tier } from './types'
import { getErrorMessage } from '@/tools/error-handling'

export const useErrorHandling = () => {
  const errorContext = useContext(ErrorBoundaryContext)

  const trackError = useCallback(
    (error: unknown) => {
      if (!onError) {
        return
      }
      onError(error, {
        customContext: errorContext?.customContext || {},
        errorBoundary: errorContext?.id || '__UNKNOWN__',
        owner: errorContext?.owner || ('__UNKNOWN__' as TeamAcronym),
        tier: errorContext?.tier || ('__UNKNOWN__' as unknown as Tier),
      })
    },
    [errorContext]
  )

  const enrichAndThrowError = useCallback(
    (error: unknown) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const enrichedError: Record<PropertyKey, any> =
        error instanceof Error
          ? error
          : new Error(typeof error === 'object' ? JSON.stringify(error) : getErrorMessage(error))

      enrichedError.errorBoundaryContext = {
        customContext: errorContext?.customContext || {},
        errorBoundary: errorContext?.id || '__UNKNOWN__',
        owner: errorContext?.owner || ('__UNKNOWN__' as TeamAcronym),
        tier: errorContext?.tier || ('__UNKNOWN__' as unknown as Tier),
      }

      throw enrichedError
    },
    [errorContext]
  )

  /**
   *
   * Captures errors thrown by the decorated function, enriches them with the boundary context,
   * and rethrows them. The error event will captured by the RUM SDK and reported.
   *
   */
  const withErrorTracking = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    <T extends Function>(fn: T): T =>
      ((...args: unknown[]) => {
        try {
          const result = fn(...args)
          if (result instanceof Promise) {
            result.catch((err: unknown) => enrichAndThrowError(err))
          }
          return result
          // eslint-disable-next-line @glovo/custom-rules/always-forward-error
        } catch (err) {
          enrichAndThrowError(err as Error)
        }
      }) as unknown as T,
    [enrichAndThrowError]
  )

  /**
   *
   * Instantaneous execution of the provided method with error tracking.
   * Useful for useEffect hooks.
   *
   */
  const execWithErrorTracking = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <T extends (...args: any[]) => any>(fn: T): void => {
      const decoratedMethod = withErrorTracking(fn)
      decoratedMethod()
    },
    [withErrorTracking]
  )

  return {
    trackError,
    withErrorTracking,
    execWithErrorTracking,
  }
}
